/**
 * 画面一覧。`listScreens` の全件を、業務マップと同じレーンの形 ステージ │ 画面 の 2 本で出す
 * （引継書 §3.3「画面一覧」）。
 *
 * **ステージから引く。** ステージのカードを押すと、右にそのステージの画面が全部出る。同じカードを
 * もう一度押すと外れ、外すと絞った画面が全部出る。どのステージにも入らない画面は
 * ステージのレーンの最後の「ステージ未定」（点線）に集め、ステージのカードとは破線 1 本で分ける。
 * 区切りに名前は付けない — 「どのステージにも入らない」という導出の説明は内部の実装の話に当たる
 * （Dhik 2026-09-28）。初めて開いたときは、画面を持つ最初の
 * ステージを選んでおく（未決 U4 はモックの今の置き方）。案件のときに「変更したものだけ見る」へ切り替えて、選んで
 * いるステージに出せる画面が無くなったら、出せる画面を持つ最初のステージ（ステージ未定を含むレーンの
 * 並び）へ選択を移し、どこにも無ければ外す（`firstStageWithScreensOf`）。「全体を見る」で戻しても選択は動かさない。
 *
 * **絞り込みは縦 3 段（ドメイン ／ アクター ／ 検索）で、数字を出さない。** 入った直後に見えるのは
 * 見出し・絞り込み・ステージと画面のカードの名前（画面はドメインの札も）だけにする（Dhik 2026-09-29
 * 「本当に出すべき情報を 1 or 2 個にするぐらい情報を絞ってほしい」）。レーンの見出しと件数・選択中の
 * ステージの名前・絞った件数は描かず、ステージの「画面 N 件」や画面の URL パターン・種類・未翻訳は
 * カードを指すと出す文字（title）へ畳む。
 * ドメイン・アクター・検索・ステージ（案件のときは「変更したものだけ見る」も）は同時に効き、絞った画面が 0 件の
 * ステージのカードは出さない（選んでいるものは残す）。
 *
 * **並びと選択肢は読取が決める。** 画面は遷移の順、ステージは鎖の順で返ってくるので、ここでは
 * 並べ直さない。ドメインの選択肢も読取が返す（`domains`）。アクターの選択肢は画面が見るアクターを
 * 集めたもので、値を画面に写していない。ステージ・ドメイン・アクターの導出は共通部
 * （`screen-derivations.ts`）が持ち、画面の側では計算しない。
 *
 * 絞り込みをクライアント側でやるのは、`listScreens` が**絞り込みの入力を持たない**ためである
 * （入力は表示言語と案件だけ）。95 件は全件返して構わない規模と決まっている。
 *
 * **画面のカードは押せず、画面詳細へはカード右上の「詳細」でだけ行く**（Dhik 2026-09-30「カードを
 * 押したときには何も起きないようにして。詳細を見るときには詳細ボタンをクリックに統一して」。以前は
 * カード全体を押すと移る例外だった）。「詳細」は業務マップと同じく、カードに指を載せたときだけ見せる
 * （`controlsOnHover`）。詳細へは案件番号を引き継ぐ — 落とすと、案件を適用した後の姿を見に行ったつもりで
 * マスターが開く。`?pj=` は案件を適用した後の一覧を出す。
 *
 * **案件のときは、案件が 追加 ／ 変更 ／ 削除 した画面のカードに業務マップのカードと同じ札と地色を
 * 付ける**（Dhik 2026-09-29「画面一覧でも変更されたものに画面マップと同じように色、タグを付けて
 * ほしい」）。札と地色は `LaneBoard` がカードの `diff` から描くので、ここは印を渡すだけにする。部品だけが
 * 変わった画面（読取の `descendant`）には付けない — 業務マップの「配下に変更」を消したのと揃える
 * （Dhik 2026-09-29「PJモードの業務マップでも配下に変更を出さず、色もつけないでほしい」）。読取の変わり方を
 * カードの印へ畳むのは `pjLaneMarkOf` の 1 か所に任せる。
 *
 * **案件のときは見出しの行の右端に「変更したものだけ見る」のトグルを置く**（`PjChangedOnlyToggle`。Dhik 2026-09-29
 * 「PJモードのとき業務マップ、画面一覧の右上に全体を出すか変更されたものだけを出すかのトグルボタンを
 * つけてほしい」）。押している間は文言が「全体を見る」に替わる。既定は全体で、選択は URL に載せない。
 * 押している間は案件で変わった画面
 * （`usePjScreenChanges`。部品の変更は API が持ち主の画面に寄せてある）だけに絞る。案件で変わった
 * 画面は一覧と一緒に揃えてから描く — 揃う前に描くと「変更したものだけ見る」が 0 件に見える。
 *
 * 名前が空の画面は「未設定」「未翻訳」を主表記にする（`displayLabel`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧を、ステージ │ 画面 のレーンとドメイン・アクター・検索（案件のときは「変更したものだけ見る」のトグルも）の絞り込みで出し、案件のときは案件が追加・変更・削除した画面のカードに業務マップと同じ札と地色を付け、画面のカードの「詳細」から画面詳細へ繋ぐ。
 * @graph-connects useListScreens [calls] 画面の一覧の全件取得
 * @graph-connects usePjScreenChanges [calls] 案件で変わった画面の取得
 */
import type {
  ScreenDomain,
  ScreenListItem,
  ScreenListStage,
  ScreenRef,
} from '@cortex/domain-spec-console';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LaneBoard,
  type Lane,
  type LaneCard,
  type LaneItem,
} from '../components/spec/LaneBoard.js';
import { ErrorState } from '../components/spec/fallback-states.js';
import { PjChangedOnlyToggle } from '../components/spec/PjChangedOnlyToggle.js';
import type { SpecDiffState } from '../components/spec/SpecDiffState.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { usePjScreenChanges } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { useListScreens } from '../hooks/queries/spec-graph-element-screens-queries.js';
import { elementPjNumberOf } from '../lib/pj-mode.js';
import { screenCardDetail } from './lane-card-details.js';
import { SCREENS_ROUTE } from './route-paths.js';
import { pjLaneMarkOf, type PjLaneMarkIndex } from './pj-lane-marks.js';
import { pjTextOf } from './pj-search.js';
import { joinHint, untranslatedHint } from './screen-hints.js';

/**
 * 「ステージ未定」のカードの id。ステージの id は 1 以上なので 0 と重ならない。
 * @graph-connects none
 */
export const UNDECIDED_STAGE_ID = 0;

/** ステージのレーンの幅。名前と件数しか載らない細い段なので固定する。 @graph-connects none */
const STAGE_LANE_WIDTH = '260px';

/**
 * 画面のカードを格子に並べる 1 列の最小幅。1440 幅で 3 列、1280 幅で 2 列になる値。
 * @graph-connects none
 */
const SCREEN_GRID_MIN_COLUMN = '240px';

/** ローディング時に出すスケルトンの行数。 @graph-connects none */
const SKELETON_ROWS = 6;

/**
 * 画面の絞り込み（ドメイン・アクター・検索）。`null` は「すべて」。
 * @graph-connects none
 */
export interface ScreenFilters {
  /** ドメインのホスト */
  domainHost: string | null;
  /** アクターの id */
  actorId: number | null;
  /** 検索の語 */
  keyword: string;
}

/**
 * 画面 1 件が検索の語に一致するか。1 つの欄で名前と URL パターンの両方を見る。
 *
 * 名前は表示中の言語の 1 つしか手元に無い。読取は選んだ言語の本文だけを返すので、別の言語の名前で
 * 引きたければ表示言語を切り替える。
 *
 * @param screen 画面 1 件
 * @param keyword 検索の語
 * @returns 一致すれば `true`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面 1 件が検索の語に、名前か URL パターンのどちらかで一致するかを判定する。
 * @graph-connects none
 */
export function matchesScreenKeyword(screen: ScreenListItem, keyword: string): boolean {
  const needle = keyword.trim().toLowerCase();
  if (needle === '') return true;
  return [screen.label, screen.pathPattern ?? ''].some((candidate) =>
    candidate.toLowerCase().includes(needle),
  );
}

/**
 * ドメイン・アクター・検索で画面を絞る。3 つは同時に効く。並びは読取の順のまま。
 *
 * @param screens 画面の全件
 * @param filters 絞り込み
 * @returns 絞った画面
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面をドメイン・アクター・検索の語で同時に絞る。
 * @graph-connects matchesScreenKeyword [calls] 名前と URL パターンの検索
 */
export function filterScreens(
  screens: readonly ScreenListItem[],
  filters: ScreenFilters,
): readonly ScreenListItem[] {
  return screens.filter(
    (screen) =>
      (filters.domainHost === null || screen.domain?.host === filters.domainHost) &&
      (filters.actorId === null ||
        screen.accessActors.some((actor) => actor.id === filters.actorId)) &&
      matchesScreenKeyword(screen, filters.keyword),
  );
}

/**
 * 画面が選んだステージに入るか。「ステージ未定」はどのステージにも入らない画面。
 *
 * @param screen 画面 1 件
 * @param stageId ステージの id（「ステージ未定」は `UNDECIDED_STAGE_ID`）
 * @returns 入れば `true`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面がステージ（またはステージ未定）に入るかを判定する。
 * @graph-connects none
 */
export function isScreenInStage(screen: ScreenListItem, stageId: number): boolean {
  return stageId === UNDECIDED_STAGE_ID
    ? screen.stageIds.length === 0
    : screen.stageIds.includes(stageId);
}

/**
 * アクターの絞り込みの選択肢。画面が見るアクターを重ならないよう集め、id の若い順に並べる。
 *
 * @param screens 画面の全件
 * @returns アクターの並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面が見るアクターを集めて、アクターの絞り込みの選択肢にする。
 * @graph-connects none
 */
export function screenActorsOf(screens: readonly ScreenListItem[]): readonly ScreenRef[] {
  const byId = new Map<number, ScreenRef>();
  for (const screen of screens) {
    for (const actor of screen.accessActors) byId.set(actor.id, actor);
  }
  return [...byId.values()].sort((left, right) => left.id - right.id);
}

/**
 * 初めて開いたときに選んでおくステージ。画面を持つ最初のステージ（鎖の順）で、どのステージも画面を
 * 持たなければ選ばない。
 *
 * @param stages ステージ（鎖の順）
 * @param screens 画面の全件
 * @returns ステージの id。選ばないなら `null`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面一覧を開いたときに選んでおくステージ（画面を持つ最初のステージ）を決める。
 * @graph-connects isScreenInStage [calls] 画面がステージに入るか
 */
export function initialStageOf(
  stages: readonly ScreenListStage[],
  screens: readonly ScreenListItem[],
): number | null {
  const first = stages.find((stage) =>
    screens.some((screen) => isScreenInStage(screen, stage.stageId)),
  );
  return first?.stageId ?? null;
}

/**
 * 出せる画面を持つ最初のステージ。並びはステージのレーンと同じ（鎖の順のステージ → ステージ未定）。
 *
 * 「変更したものだけ見る」へ切り替えたとき、選んでいたステージに出せる画面が無ければここへ選択を移す。
 * `initialStageOf` と違ってステージ未定も候補に入れる — 変わった画面がステージ未定にしか無いとき、
 * 選択を外すと 1 件しか無い変わった画面を探しに行かせることになる。初めて開いたときの選び方
 * （未決 U4）は変えない。
 *
 * @param stages ステージ（鎖の順）
 * @param screens 出せる画面（絞った画面）
 * @returns ステージの id（ステージ未定は `UNDECIDED_STAGE_ID`）。どこにも無ければ `null`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 出せる画面を持つ最初のステージを、ステージのレーンの並び（ステージ未定を最後に含む）で決める。
 * @graph-connects initialStageOf [calls] 画面を持つ最初のステージ
 * @graph-connects isScreenInStage [calls] 画面がステージ未定に入るか
 */
export function firstStageWithScreensOf(
  stages: readonly ScreenListStage[],
  screens: readonly ScreenListItem[],
): number | null {
  const first = initialStageOf(stages, screens);
  if (first !== null) return first;
  return screens.some((screen) => isScreenInStage(screen, UNDECIDED_STAGE_ID))
    ? UNDECIDED_STAGE_ID
    : null;
}

/**
 * 画面一覧のページ。
 *
 * **master と案件で部品を分ける。** 案件のときだけ案件で変わった画面を読むので、同じ部品に置くと
 * フックを呼ぶかどうかが描画のたびに変わる（業務マップの `OverviewPage` と同じ形）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面一覧の案件番号を URL から読み、master か案件を当てた一覧の本体を出し分ける。
 * @graph-connects elementPjNumberOf [calls] URL の案件番号を読取が受ける number へ畳む
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 * @graph-connects ScreensMasterBody [calls] master の一覧の本体
 * @graph-connects ScreensPjBody [calls] 案件を当てた一覧の本体
 */
export function ScreensPage() {
  const pj = pjTextOf(useSearch({ from: SCREENS_ROUTE }).pj);
  const pjNumber = elementPjNumberOf(pj);

  return pjNumber === undefined ? (
    <ScreensMasterBody pj={pj} />
  ) : (
    <ScreensPjBody pj={pj} pjNumber={pjNumber} />
  );
}

/** `ScreensMasterBody` の props。 @graph-connects none */
interface ScreensMasterBodyProps {
  /** URL の案件番号の綴り。数字にならない綴りでも詳細へのリンクへ引き継ぐ */
  pj?: string;
}

/**
 * master の一覧の本体。「変更したものだけ見る」のトグルは置かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧を master の姿で取得して出す。
 * @graph-connects useListScreens [calls] 画面の一覧の全件取得
 * @graph-connects ScreensBody [calls] 失敗・読み込み中・レーンの出し分け
 */
function ScreensMasterBody({ pj }: ScreensMasterBodyProps) {
  const { data, isLoading, error } = useListScreens();

  return <ScreensBody data={data} isLoading={isLoading} error={error} pj={pj} screenMarks={null} />;
}

/** `ScreensPjBody` の props。 @graph-connects none */
interface ScreensPjBodyProps {
  /** URL の案件番号の綴り。詳細へのリンクへ引き継ぐ */
  pj?: string;
  /** 当てる案件番号 */
  pjNumber: number;
}

/**
 * 案件を当てた一覧の本体。一覧と案件で変わった画面の 2 本が揃ってから描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧を案件を当てた姿で取得し、案件で変わった画面を画面の id から印を引く索引にして揃えてから出す。
 * @graph-connects useListScreens [calls] 案件を当てた画面の一覧の取得
 * @graph-connects usePjScreenChanges [calls] 案件で変わった画面の取得
 * @graph-connects ScreensBody [calls] 失敗・読み込み中・レーンの出し分け
 */
function ScreensPjBody({ pj, pjNumber }: ScreensPjBodyProps) {
  const list = useListScreens(pjNumber);
  const changes = usePjScreenChanges(pjNumber);
  const screenMarks: PjLaneMarkIndex = useMemo(
    () => new Map((changes.data ?? []).map((change) => [change.screenId, change.changeKind])),
    [changes.data],
  );

  return (
    <ScreensBody
      data={changes.data === undefined ? undefined : list.data}
      isLoading={list.isLoading || changes.isLoading}
      error={list.error ?? changes.error}
      pj={pj}
      screenMarks={screenMarks}
    />
  );
}

/** `ScreensBody` の props。 @graph-connects none */
interface ScreensBodyProps {
  data: ReturnType<typeof useListScreens>['data'];
  isLoading: boolean;
  error: unknown;
  /** URL の案件番号の綴り。詳細へのリンクへ引き継ぐ */
  pj?: string;
  /** 案件で変わった画面の id から印を引く索引。master なら `null`（「変更したものだけ見る」のトグルと印を置かない） */
  screenMarks: PjLaneMarkIndex | null;
}

/**
 * 本体（失敗 / 読み込み中 / 絞り込みとレーン）の出し分け。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧を失敗・読み込み中・絞り込みとレーンの 3 状態で出し分ける。
 * @graph-connects ScreensBoard [calls] 絞り込みとレーン
 */
function ScreensBody({ data, isLoading, error, pj, screenMarks }: ScreensBodyProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {error !== null && error !== undefined ? (
        <>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('screens.title')}
          </h1>
          <ErrorState error={error} />
        </>
      ) : isLoading || data === undefined ? (
        <>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('screens.title')}
          </h1>
          <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('common.loading')}>
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton key={index} variant="rectangular" height="2rem" />
            ))}
          </div>
        </>
      ) : (
        <ScreensBoard
          screens={data.screens}
          stages={data.stages ?? []}
          domains={data.domains ?? []}
          pj={pj}
          screenMarks={screenMarks}
        />
      )}
    </div>
  );
}

/** `ScreensBoard` の props。 @graph-connects none */
interface ScreensBoardProps {
  /** 画面の全件（遷移の順） */
  screens: readonly ScreenListItem[];
  /** ステージ（鎖の順） */
  stages: readonly ScreenListStage[];
  /** 選べるドメイン */
  domains: readonly ScreenDomain[];
  /** 重ねている案件番号の綴り。詳細へのリンクへ引き継ぐ */
  pj?: string;
  /**
   * 案件で変わった画面の id から印を引く索引（部品だけが変わった画面の `descendant` を含む）。
   * master なら `null`。
   *
   * `null` でなければ見出しの右端に「変更したものだけ見る」のトグルを置き、追加 ・ 変更 ・ 削除 した画面の
   * カードに印を付ける。**任意の prop にしない** — 渡し忘れは型でも lint でも落ちず、案件を当てた
   * 一覧からトグルと印だけが黙って消える。
   */
  screenMarks: PjLaneMarkIndex | null;
}

/**
 * 見出し（案件のときは右端に「変更したものだけ見る」のトグル）・絞り込み 3 段・見出しを描かないレーン 2 本。
 *
 * 「変更したものだけ見る」はドメイン・アクター・検索と同じ段で効かせる（件数・ステージのカードの出し分けは
 * 絞った画面から数える）。「変更したものだけ見る」へ切り替えて選んでいるステージに出せる画面が無くなったら、
 * 出せる画面を持つ最初のステージへ選択を移す（どこにも無ければ外す）。案件のときは、案件が追加 ・
 * 変更 ・ 削除 した画面のカードに印（札と地色）を付ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面をドメイン・アクター・検索・ステージ（案件のときは「変更したものだけ見る」も）で同時に絞り、ステージ │ 画面 のレーンに描き（案件のときは追加・変更・削除した画面のカードに印を付ける）、「変更したものだけ見る」へ切り替えて選んだステージが空になったら出せる画面を持つステージへ選択を移す。
 * @graph-connects PjChangedOnlyToggle [calls] 「変更したものだけ見る」のトグル
 * @graph-connects filterScreens [calls] ドメイン・アクター・検索で画面を絞る
 * @graph-connects isScreenInStage [calls] 画面がステージに入るか
 * @graph-connects initialStageOf [calls] 初めて開いたときに選んでおくステージ
 * @graph-connects firstStageWithScreensOf [calls] 「変更したものだけ見る」へ切り替えたときに選択を移す先
 * @graph-connects screenActorsOf [calls] アクターの絞り込みの選択肢
 * @graph-connects screenCard [calls] 画面のカード
 * @graph-connects screenCardMark [calls] 画面のカードの印
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 * @graph-connects joinHint [calls] ステージのカードの件数と未翻訳を指すと出す文字に畳む
 * @graph-connects untranslatedHint [calls] 未翻訳の印の文字
 */
function ScreensBoard({ screens, stages, domains, pj, screenMarks }: ScreensBoardProps) {
  const { t } = useTranslation();
  const pjNumber = elementPjNumberOf(pj);
  const [domainHost, setDomainHost] = useState<string | null>(null);
  const [actorId, setActorId] = useState<number | null>(null);
  const [keyword, setKeyword] = useState('');
  const [chosenStage, setChosenStage] = useState<number | null | undefined>(undefined);
  const [changedOnly, setChangedOnly] = useState(false);

  const selected = chosenStage === undefined ? initialStageOf(stages, screens) : chosenStage;
  const filtered = filterScreens(screens, { domainHost, actorId, keyword });
  const changed =
    screenMarks === null ? filtered : filtered.filter((screen) => screenMarks.has(screen.screenId));
  const pool = changedOnly ? changed : filtered;

  /*
   * 「変更したものだけ見る」へ切り替えたとき、選んでいるステージに出せる画面が無ければ、出せる画面を持つ最初の
   * ステージへ選択を移す（どこにも無ければ外す）。そのままだと、隠れたステージを選んだまま画面の
   * レーンが空になる。「全体を見る」で戻したときは選択を動かさない。
   */
  const changeScope = (nextChangedOnly: boolean) => {
    setChangedOnly(nextChangedOnly);
    if (
      nextChangedOnly &&
      selected !== null &&
      !changed.some((screen) => isScreenInStage(screen, selected))
    ) {
      setChosenStage(firstStageWithScreensOf(stages, changed));
    }
  };
  const countIn = (stageId: number) =>
    pool.filter((screen) => isScreenInStage(screen, stageId)).length;

  const stageNames = new Map(
    stages.map((stage) => [stage.stageId, displayLabel(stage.label, t, stage.untranslated)]),
  );
  const stageCards: LaneCard[] = stages
    .map((stage) => ({ stage, count: countIn(stage.stageId) }))
    .filter(({ stage, count }) => count > 0 || stage.stageId === selected)
    .map(({ stage, count }) => ({
      id: stage.stageId,
      title: displayLabel(stage.label, t, stage.untranslated),
      hint: joinHint([
        t('screens.stageScreenCount', { count }),
        untranslatedHint(stage.label, stage.untranslated, t),
      ]),
      diff: 'unchanged',
    }));
  const undecidedCount = countIn(UNDECIDED_STAGE_ID);
  const undecidedItems: LaneItem[] =
    undecidedCount > 0 || selected === UNDECIDED_STAGE_ID
      ? [
          { kind: 'separator', separatorKey: 'undecided' },
          {
            id: UNDECIDED_STAGE_ID,
            title: t('screens.stageUndecided'),
            hint: t('screens.stageScreenCount', { count: undecidedCount }),
            dashed: true,
            diff: 'unchanged',
          },
        ]
      : [];

  const shown =
    selected === null ? pool : pool.filter((screen) => isScreenInStage(screen, selected));

  /*
   * レーンの見出しと件数は描かない（カードの中身でレーンの種類が分かり、数は問いに答えない）。
   * ステージのレーンの空の文も出さない — 画面のレーンの空の文が同じことを言う。
   */
  const lanes: readonly Lane[] = [
    {
      key: 'stage',
      title: t('screens.laneStage'),
      count: stageCards.length,
      header: false,
      selectedId: selected ?? undefined,
      onSelect: (id) => {
        setChosenStage(selected === id ? null : id);
      },
      cards: [...stageCards, ...undecidedItems],
      width: STAGE_LANE_WIDTH,
    },
    {
      key: 'screen',
      title: t('screens.laneScreen'),
      count: shown.length,
      header: false,
      emptyMessage: t('screens.emptyLane'),
      cards: shown.map((screen) =>
        screenCard({
          screen,
          stageNames,
          mark: screenCardMark(screenMarks, screen.screenId),
          pjNumber,
          t,
        }),
      ),
      gridMinColumn: SCREEN_GRID_MIN_COLUMN,
    },
  ];

  return (
    <>
      <header className="flex flex-wrap items-center gap-4">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('screens.title')}
        </h1>
        {screenMarks !== null && (
          <PjChangedOnlyToggle changedOnly={changedOnly} onChange={changeScope} />
        )}
      </header>

      <div className="flex flex-col gap-2 border-b border-[var(--color-border)] pb-3">
        <FilterRow label={t('screens.filterDomain')}>
          <ToggleGroup
            label={t('screens.filterDomain')}
            value={domainHost}
            options={domains.map((domain) => ({
              value: domain.host,
              label: domain.label,
              title: domain.host,
            }))}
            allLabel={t('screens.filterAll')}
            onSelect={setDomainHost}
          />
        </FilterRow>
        <FilterRow label={t('screens.filterActor')}>
          <ToggleGroup
            label={t('screens.filterActor')}
            value={actorId}
            options={screenActorsOf(screens).map((actor) => ({
              value: actor.id,
              label: displayLabel(actor.label, t),
            }))}
            allLabel={t('screens.filterAll')}
            onSelect={setActorId}
          />
        </FilterRow>
        <FilterRow>
          <input
            type="search"
            value={keyword}
            placeholder={t('screens.searchPlaceholder')}
            aria-label={t('screens.searchPlaceholder')}
            onChange={(event) => {
              setKeyword(event.target.value);
            }}
            className="h-8 w-[300px] rounded-sm border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 text-sm text-[var(--color-text-primary)]"
          />
        </FilterRow>
      </div>

      <LaneBoard lanes={lanes} controlsOnHover />
    </>
  );
}

/**
 * 画面のカードに付ける印。案件が追加 ・ 変更 ・ 削除 した画面はその印、それ以外（案件なし・変わって
 * いない・部品だけが変わった `descendant`）は変更なし。
 *
 * 部品だけが変わった画面に印を付けないのは、PJ モードのどの画面にも「配下に変更」を出さないため
 * である（Dhik 2026-09-29「PJモードの業務マップでも配下に変更を出さず、色もつけないでほしい」）。
 * 変更なしに畳むのは索引から印を引く `pjLaneMarkOf` の 1 か所で、ここは案件なしを振り分けるだけ。
 *
 * @param marks 画面の id から変わり方を引く索引。案件なしは `null`
 * @param screenId 画面の id
 * @returns カードの印（追加 ・ 変更 ・ 削除 ・ 変更なし）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わった画面の索引から、画面のカードに付ける印（追加・変更・削除のどれか、それ以外は変更なし）を決める。
 * @graph-connects pjLaneMarkOf [calls] 索引から印を引く（配下に変更は変更なしに畳む）
 */
export function screenCardMark(marks: PjLaneMarkIndex | null, screenId: number): SpecDiffState {
  return marks === null ? 'unchanged' : pjLaneMarkOf(marks, screenId);
}

/** `screenCard` の入力。 @graph-connects none */
interface ScreenCardInput {
  /** 画面 1 件 */
  screen: ScreenListItem;
  /** ステージの id → 表示名 */
  stageNames: ReadonlyMap<number, string>;
  /** カードの印（`screenCardMark` が決める） */
  mark: SpecDiffState;
  /** 詳細へ引き継ぐ案件番号。master なら undefined */
  pjNumber: number | undefined;
  /** 文言 */
  t: (key: string, params?: Record<string, unknown>) => string;
}

/**
 * 画面のカード（名前 ／ ドメインの札）。見えるのは名前とドメインの札（指すとホスト）と、案件のときの
 * 追加 ・ 変更 ・ 削除 の札と地色だけで、URL パターン（未設定ならその旨）・種類（コールバックは説明つき）・
 * 未翻訳・複数のステージに入るなら「ステージ N つ: ステージ名」は、カードを指すと出す文字（title）へ畳む。
 * どのステージにも入らないことは「ステージ未定」のカードを選べば分かるので書かない。
 * コールバックは人が見ない中継画面なので薄い字にする。入口の札は導く規則が無いので出さない。
 * 画面詳細へは右上の「詳細」で行く（カードそのものは押せない）。
 *
 * @param input 画面 1 件・ステージの名前・カードの印・引き継ぐ案件番号・文言
 * @returns 画面のカード
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面 1 件をカード（名前・ドメインの札・案件での変更の印・「詳細」）にし、URL パターン・種類・未翻訳・入るステージを指すと出す文字に畳む。
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 * @graph-connects joinHint [calls] 指すと出す文字に畳む
 * @graph-connects untranslatedHint [calls] 未翻訳の印の文字
 * @graph-connects screenCardDetail [calls] 画面のカードの「詳細」の行き先
 */
function screenCard({ screen, stageNames, mark, pjNumber, t }: ScreenCardInput): LaneCard {
  const title = displayLabel(screen.label, t, screen.untranslated);
  const callback = screen.kind === 'callback';
  const kind =
    screen.kind === null
      ? null
      : callback
        ? `${t(`screens.kind.${screen.kind}`)}: ${t('screens.callbackTitle')}`
        : t(`screens.kind.${screen.kind}`);
  const stages =
    screen.stageIds.length > 1
      ? `${t('screens.stageCountBadge', { count: screen.stageIds.length })}: ${screen.stageIds
          .map((stageId) => stageNames.get(stageId) ?? t('spec.unset'))
          .join(' ／ ')}`
      : null;
  return {
    id: screen.screenId,
    title,
    hint: joinHint([
      screen.pathPattern ?? t('screens.pathPatternEmpty'),
      kind,
      untranslatedHint(screen.label, screen.untranslated, t),
      stages,
    ]),
    faint: callback,
    diff: mark,
    detail: screenCardDetail(screen.screenId, pjNumber, t),
    badges:
      screen.domain === null ? undefined : (
        <Badge
          variant="default"
          size="sm"
          data-screen-domain={screen.domain.host}
          title={screen.domain.host}
        >
          {screen.domain.label}
        </Badge>
      ),
  };
}

/** `FilterRow` の props。 @graph-connects none */
interface FilterRowProps {
  /** 段の見出し。検索の段は入力欄の薄い字で用途が分かるので省き、見出しの幅だけ空ける */
  label?: string;
  children: React.ReactNode;
}

/**
 * 絞り込みの 1 段。見出しの幅を揃えて、ボタンと検索欄の左端を縦に揃える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞り込みの 1 段を、幅を揃えた見出しと中身で描く。
 * @graph-connects none
 */
function FilterRow({ label, children }: FilterRowProps) {
  return (
    <div data-screens-filter-row className="flex items-center gap-3">
      <span
        aria-hidden={label === undefined ? true : undefined}
        className="w-16 flex-none text-xs font-bold text-[var(--color-text-muted)]"
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/** 切替の選択肢 1 つ。 @graph-connects none */
interface ToggleOption<T> {
  value: T;
  label: string;
  /** 指を載せると出す文字（ドメインのホスト） */
  title?: string;
}

/** `ToggleGroup` の props。 @graph-connects none */
interface ToggleGroupProps<T> {
  /** 読み上げる段の名前 */
  label: string;
  /** 選んでいる値。`null` は「すべて」 */
  value: T | null;
  options: readonly ToggleOption<T>[];
  /** 「すべて」の文言 */
  allLabel: string;
  onSelect: (value: T | null) => void;
}

/**
 * 絞り込みの切替（すべて＋選択肢）。ボタンの文字は名前だけで、件数を出さない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞り込みの切替 1 段を、すべて＋選択肢のボタンの並びで描く。
 * @graph-connects none
 */
function ToggleGroup<T>({ label, value, options, allLabel, onSelect }: ToggleGroupProps<T>) {
  const buttons: readonly (ToggleOption<T | null> & { key: string })[] = [
    { key: 'all', value: null, label: allLabel },
    ...options.map((option) => ({ ...option, key: String(option.value) })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      {buttons.map((option) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.value}
          data-filter-option={option.key}
          title={option.title}
          onClick={() => {
            onSelect(option.value);
          }}
          className={
            value === option.value
              ? 'rounded-sm border border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] px-2 py-0.5 text-xs text-[var(--color-bg-base)]'
              : 'rounded-sm border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
